import { ThemeEvents } from '@theme/events';

// Island Girl : filtre des photos selon la taille portée par le mannequin.
// Une photo est étiquetée en ajoutant « en taille S », « en taille M » ou « en taille L »
// dans son texte alternatif (ou « en tailles S et M » pour une photo de duo). Les photos sans étiquette (groupe, produit seul) restent
// visibles pour toutes les tailles. Si aucune photo n'existe pour la taille choisie,
// toute la galerie reste affichée. Le filtre s'applique au chargement de la page et à chaque changement de taille.
const SIZE_TAG = /en tailles?\s+((?:XS|XL|S|M|L)(?:\s*(?:,|et|\+|\/)\s*(?:XS|XL|S|M|L))*)\b/i;

function getSlideSizes(slide) {
  const img = slide.querySelector('img[alt]');
  const match = img?.getAttribute('alt')?.match(SIZE_TAG);
  if (!match) return null;
  return match[1].toUpperCase().match(/XS|XL|S|M|L/g);
}

function getSelectedSize(html) {
  try {
    const json = html?.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!json) return null;
    const variant = JSON.parse(json);
    const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    for (const value of values) {
      const match = String(value).trim().match(/^(XS|XL|S|M|L)\b/i);
      if (match) return match[1].toUpperCase();
    }
  } catch (e) {
    console.warn('Filtre photos par taille :', e);
  }
  return null;
}

// Renvoie les index des photos à masquer pour une taille donnée (liste vide = on garde tout)
function getHiddenIndexes(slides, size) {
  if (!size) return [];
  const sizes = slides.map(getSlideSizes);
  if (!sizes.some((list) => list?.includes(size))) return [];
  const hidden = [];
  sizes.forEach((list, index) => {
    if (list && !list.includes(size)) hidden.push(index);
  });
  return hidden;
}

function filterGalleryBySize(gallery, size) {
  const mainSlides = Array.from(gallery.querySelectorAll('[data-ref="main-slider"] .splide__list > li'));
  const thumbSlides = Array.from(gallery.querySelectorAll('[data-ref="thumbnail-slider"] .splide__list > li'));
  getHiddenIndexes(mainSlides, size).forEach((index) => {
    mainSlides[index]?.remove();
    thumbSlides[index]?.remove();
  });
}

class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
    this.applyInitialSizeFilter(0);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
  }

  // Au chargement : le carrousel est déjà monté, on retire les photos via l'API du slider
  applyInitialSizeFilter(tries) {
    const slider = this.querySelector('slider-component');
    const main = slider?.querySelector('[data-ref="main-slider"]');
    if (!slider || !main) return;
    if (!slider.slider || !main.classList.contains('is-initialized')) {
      if (tries < 50) setTimeout(() => this.applyInitialSizeFilter(tries + 1), 100);
      return;
    }
    try {
      const size = getSelectedSize(this.closest('.shopify-section, dialog'));
      const slides = Array.from(main.querySelectorAll('.splide__list > li:not(.splide__slide--clone)'));
      const hidden = getHiddenIndexes(slides, size);
      if (!hidden.length) return;
      slider.slider.remove(hidden);
      slider.thumbnailSlider?.remove(hidden);
      slider.slider.go(0);
      slider.thumbnailSlider?.refresh();
    } catch (e) {
      console.warn('Filtre photos par taille (chargement) :', e);
    }
  }

  updateMediaGallery = (event) => {
    const html = event.detail.data.html;
    const newMediaGallery = html.querySelector('product-media-gallery slider-component');
    const currentMediaGallery = this.querySelector('slider-component');

    if (!newMediaGallery || !currentMediaGallery) return;

    filterGalleryBySize(newMediaGallery, getSelectedSize(html));

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      currentMediaGallery.replaceWith(newMediaGallery);

      // Réactive les boutons AR (data-shopify-xr) présents dans le nouveau HTML
      window.ShopifyXR?.setupXRElements();
    }
  };
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
